import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ContactDirectoryItem,
  ContactResponse,
  CreateContactInput,
  CustomerType,
  ItemResponse,
  ListResponse,
  UpdateContactInput,
} from '@proerp/shared';
import { apiRequest } from '@/lib/apiClient';

const contactsKey = (customerId: string) => ['customers', 'detail', customerId, 'contacts'] as const;

export function useContacts(customerId: string) {
  return useQuery({
    queryKey: contactsKey(customerId),
    queryFn: () =>
      apiRequest<ListResponse<ContactResponse>>(`/v1/customers/${customerId}/contacts`),
    enabled: Boolean(customerId),
  });
}

export function useCreateContact(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateContactInput) =>
      apiRequest<ItemResponse<ContactResponse>>(`/v1/customers/${customerId}/contacts`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: contactsKey(customerId) }),
  });
}

export function useUpdateContact(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateContactInput }) =>
      apiRequest<ItemResponse<ContactResponse>>(`/v1/customers/${customerId}/contacts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: contactsKey(customerId) }),
  });
}

export function useDeleteContact(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ data: { id: string } }>(`/v1/customers/${customerId}/contacts/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: contactsKey(customerId) }),
  });
}

const directoryKey = ['contacts', 'directory'] as const;

/** Globales Ansprechpartner-Verzeichnis (CRM-Submenü). */
export interface ContactDirectoryQueryInput {
  q?: string;
  page?: number;
  pageSize?: number;
  customerType?: CustomerType;
  isPrimary?: boolean;
  sort?: string;
}

function buildDirectoryQueryString(input: ContactDirectoryQueryInput): string {
  const params = new URLSearchParams();
  params.set('page', String(input.page ?? 1));
  params.set('pageSize', String(input.pageSize ?? 500));
  if (input.q?.trim()) params.set('q', input.q.trim());
  if (input.customerType) params.set('customerType', input.customerType);
  if (input.isPrimary) params.set('isPrimary', 'true');
  if (input.sort) params.set('sort', input.sort);
  return params.toString();
}

/** Nicht-Hook-Fetch einer Verzeichnis-Seite (für „Export alles" via collectAllPages). */
export function fetchContactDirectoryPage(
  input: ContactDirectoryQueryInput,
): Promise<ListResponse<ContactDirectoryItem>> {
  return apiRequest<ListResponse<ContactDirectoryItem>>(`/v1/contacts?${buildDirectoryQueryString(input)}`);
}

export function useContactDirectory(input: ContactDirectoryQueryInput) {
  return useQuery({
    queryKey: [...directoryKey, input] as const,
    queryFn: () => apiRequest<ListResponse<ContactDirectoryItem>>(`/v1/contacts?${buildDirectoryQueryString(input)}`),
    placeholderData: keepPreviousData,
  });
}

/** Einzelner Ansprechpartner (Verzeichnis-Detail, inkl. Kunde/Lieferant). */
export function useDirectoryContact(id: string) {
  return useQuery({
    queryKey: [...directoryKey, 'detail', id] as const,
    queryFn: () => apiRequest<ItemResponse<ContactDirectoryItem>>(`/v1/contacts/${id}`),
    enabled: Boolean(id),
  });
}

/** Anlegen/Ändern/Löschen aus dem Verzeichnis — Adresse (customerId) wird je Aufruf übergeben. */
export function useCreateContactFor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, input }: { customerId: string; input: CreateContactInput }) =>
      apiRequest<ItemResponse<ContactResponse>>(`/v1/customers/${customerId}/contacts`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    // Verzeichnis UND Ansprechpartner-Register der Kundenakte — beide nutzen dieses Formular.
    onSuccess: (_result, { customerId }) => {
      void queryClient.invalidateQueries({ queryKey: directoryKey });
      void queryClient.invalidateQueries({ queryKey: contactsKey(customerId) });
    },
  });
}

export function useUpdateContactFor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      customerId,
      id,
      input,
    }: {
      customerId: string;
      id: string;
      input: UpdateContactInput;
    }) =>
      apiRequest<ItemResponse<ContactResponse>>(`/v1/customers/${customerId}/contacts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: (_result, { customerId, input }) => {
      void queryClient.invalidateQueries({ queryKey: directoryKey });
      void queryClient.invalidateQueries({ queryKey: contactsKey(customerId) });
      // Umgezogen: auch die neue Adresse lädt ihre Ansprechpartner neu.
      if (input.customerId && input.customerId !== customerId) {
        void queryClient.invalidateQueries({ queryKey: contactsKey(input.customerId) });
      }
    },
  });
}

export function useDeleteContactFor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, id }: { customerId: string; id: string }) =>
      apiRequest<{ data: { id: string } }>(`/v1/customers/${customerId}/contacts/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: directoryKey }),
  });
}
